import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { nachAktivierung } from "@/lib/prerendering";
import { useCookieConsent } from "@/hooks/useCookieConsent";

// Nur Marketing-Seiten. Der eingeloggte Kundenbereich (/dashboard) ist bewusst NICHT
// dabei (Noah, 17.09.2026): Session-Recording bringt dort kaum Erkenntnis, kostet aber
// 154 KB auf dem kritischen Ladepfad und nimmt Namen, Adressen und Hundedaten mit auf.
const TRACKED_PATHS = ["/", "/gassi-service", "/schnuppertag", "/faq", "/about"];

/**
 * Contentsquare zeichnet Sitzungen auf (Klicks, Scrollen, Mausbewegung) und gehört
 * deshalb wie Clarity in die Kategorie Statistik: nur mit Einwilligung.
 * Bis 02.10.2026 lud es ohne Einwilligung, nur über den Pfadfilter (Fund aus der
 * Datenschutz-Prüfung, KA-22).
 */
export function contentsquareErlaubt(pathname: string, statistikEinwilligung: boolean | undefined): boolean {
  return statistikEinwilligung === true && TRACKED_PATHS.includes(pathname);
}

export function useContentsquare() {
  const location = useLocation();
  const { consent } = useCookieConsent();
  const statistik = consent?.analytics;

  useEffect(() => {
    const shouldTrack = contentsquareErlaubt(location.pathname, statistik);
    const scriptId = "contentsquare-script";
    const existingScript = document.getElementById(scriptId);

    if (!shouldTrack || existingScript) return;

    // Defer bis Browser idle ist — verhindert Render-Blocking auf LCP
    // (Lighthouse 2026-05-11: 160ms unnötig blockend für ContentSquare)
    const loadScript = () => {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://t.contentsquare.net/uxa/3960b0c1d2a48.js";
      script.async = true;
      script.onerror = (e) => console.warn("[CS] Script load failed", e);
      document.head.appendChild(script);
    };

    // Vorab gerenderte Seiten (Speculation Rules, Paket 1d): Contentsquare startet
    // beim Laden eine Session-Aufzeichnung und zählt einen Seitenaufruf. Das Skript
    // darf deshalb erst nach der Aktivierung in den <head>, sonst zeichnet es eine
    // Seite auf, die nie jemand gesehen hat. /schnuppertag ist Prerender-Ziel.
    const abbrechen = nachAktivierung(() => {
      if ("requestIdleCallback" in window) {
        (window as any).requestIdleCallback(loadScript, { timeout: 3000 });
      } else {
        setTimeout(loadScript, 1500);
      }
    });

    return abbrechen;
  }, [location.pathname, statistik]);
}
